import React, { useCallback, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import PlotlyPlot from "react-plotly.js";
import { useFormState } from "react-final-form";
import { get } from "lodash";
import classNames from "classnames";
import { getPlotData, getPlotLayout, plotConfig } from "./utils";
import { isNotEmpty } from "utils/validate";
import { renderNumberValue } from "utils/string";
import { findDictionaryItemByValue, recipeParameterUnitsType } from "shared/rsDictionary";
import { useRoundTickValueObserver } from "shared/plotly/hooks";
import Dialog from "components/Dialog";
import HtmlText from "components/HtmlText";
import ScrollBar from "components/ScrollBar";
import Loader from "components/Loader";
import ScoreIcon from "rs-components/ScoreIcon";
import { InsightFormFields } from "../../../../../../index";
import { NoUnitsType } from "store/recipe/recipe.types";
import { insightExpandedInputSetIndexSelector, previewRequestsSelector, workspaceCurrentInsightIdSelector, workspaceRecipesSelector, workspaceSimulationsSelector, } from "store/rs-workspace/selectors/rs-workspace.selectors";
import { processVariablesSelector } from "store/global/global.selectors";
import { InsightOutputVariableTypes, } from "store/rs-workspace/rs-workspace.types";
import { checkCanDisplayScore, checkScoreHasBiasedValues, getActualSourceName, getInsightPreviewOperationPending, prepareBaseScoreItem, } from "store/rs-workspace/utils/insights.utils";
import { parseComplexUnitToHtml } from "store/recipe/custom-variables.utils";
import { INSIGHT_INPUT_COLORS } from "store/rs-workspace/utils/scenario.utils";
import { DIALOG_ACTION_TITLE, InsightLabels } from "shared/labels";
import WarningIcon from "icons/validationWarning.svg";
import * as styles from "./styles.module.scss";
import * as colors from "styles/colorValues.module.scss";
const OutputGraph = ({ name, index, score, operationSummaryText, onClose }) => {
    const formState = useFormState();
    const plotRef = useRef(null);
    const workspaceSimulations = useSelector(workspaceSimulationsSelector);
    const previewRequests = useSelector(previewRequestsSelector);
    const currentInsightId = useSelector(workspaceCurrentInsightIdSelector);
    const expandedInputSetIndex = useSelector(insightExpandedInputSetIndexSelector);
    const processVariables = useSelector(processVariablesSelector);
    const recipes = useSelector(workspaceRecipesSelector);
    const inputSets = useMemo(() => get(formState.values, InsightFormFields.inputSets), [formState.values]);
    const inputSet = useMemo(() => inputSets[expandedInputSetIndex], [expandedInputSetIndex, inputSets]);
    const output = useMemo(() => get(formState.values, name), [formState.values, name]);
    const graphResult = useMemo(() => score?.score.data?.graphResult, [score?.score.data?.graphResult]);
    const pending = useMemo(() => {
        if (!inputSet || !isNotEmpty(output?.id)) {
            return false;
        }
        return getInsightPreviewOperationPending(previewRequests, currentInsightId, inputSet.id, output?.id);
    }, [currentInsightId, inputSet, output?.id, previewRequests]);
    const canDisplayScore = useMemo(() => checkCanDisplayScore(output, inputSet.inputs, processVariables, recipes), [inputSet.inputs, output, processVariables, recipes]);
    const scoreHasBiasedValues = useMemo(() => checkScoreHasBiasedValues(score?.score), [score?.score]);
    const { errored, errorMessage } = useMemo(() => prepareBaseScoreItem(score?.score, canDisplayScore), [canDisplayScore, score?.score]);
    const graphShouldBeEmpty = useMemo(() => !canDisplayScore || !operationSummaryText || pending || !score?.score?.data || !inputSet, [operationSummaryText, pending, score?.score?.data, inputSet, canDisplayScore]);
    const graphData = useMemo(() => getPlotData(graphShouldBeEmpty, graphResult, inputSet), [graphShouldBeEmpty, inputSet, graphResult]);
    const plotLayout = useMemo(() => getPlotLayout(graphShouldBeEmpty, !!errored, scoreHasBiasedValues, score?.score?.data?.result, graphResult, inputSet), [graphShouldBeEmpty, errored, score?.score, scoreHasBiasedValues, graphResult, inputSet]);
    const selectedVariableLabel = useMemo(() => {
        const { units, label, type } = output?.processVariable ?? {};
        const isComplexUnit = type === InsightOutputVariableTypes.Custom;
        let unit;
        if (!isComplexUnit) {
            const unitOption = findDictionaryItemByValue(recipeParameterUnitsType, units);
            unit = unitOption?.value === NoUnitsType.NoUnits ? undefined : unitOption?.label;
        }
        if (isComplexUnit) {
            unit = parseComplexUnitToHtml(units)?.html;
        }
        return (React.createElement(HtmlText, { targetClassName: styles["variable-value"], text: `${label} ${unit ? `(${unit})` : ""}` }));
    }, [output]);
    const getThresholdLabel = useCallback((value) => {
        const operationType = output?.operation?.type;
        if (!isNotEmpty(value) || !operationType?.value) {
            return;
        }
        return operationType.value === "CloseToOptimum" ? "Optimum" : "Threshold";
    }, [output?.operation?.type]);
    const getLineLegend = useCallback((title, lineColor, lineType, subtitle) => (React.createElement("div", { className: styles.lineLegend },
        React.createElement("span", { className: classNames(styles["lineLegend-line"], {
                [styles[`lineLegend-line-${lineType}`]]: !!lineType,
            }), style: { color: lineColor } }),
        React.createElement("p", { className: styles["lineLegend-title"] }, title),
        !!subtitle && React.createElement("p", { className: styles["lineLegend-subTitle"] }, subtitle))), []);
    const getLineSpreadLegend = useCallback((title, lineColor) => (React.createElement("div", { className: styles.lineSpreadLegend },
        React.createElement("span", { className: styles["lineSpreadLegend-spread"], style: { color: lineColor } }),
        React.createElement("p", { className: styles["lineSpreadLegend-title"] }, title))), []);
    const getSourceLineLegend = useCallback((sourceValues, withinRangeSpreadTitle, thresholdLabel) => {
        const { inputId, yValues, xValues, spreadYValues, spreadXValues } = sourceValues;
        const sourceInputSetInputIndex = inputSet.inputs.findIndex(({ id }) => id === inputId);
        const input = inputSet.inputs[sourceInputSetInputIndex];
        const lineColor = INSIGHT_INPUT_COLORS[sourceInputSetInputIndex];
        return (React.createElement(React.Fragment, null,
            xValues &&
                yValues &&
                getLineLegend(`Input ${sourceInputSetInputIndex + 1}`, lineColor, undefined, getActualSourceName(input, workspaceSimulations)),
            spreadYValues &&
                spreadXValues &&
                getLineSpreadLegend(`Offset: ${renderNumberValue(output?.operation?.parameters[0]?.value)}-
              ${renderNumberValue(output?.operation?.parameters[1]?.value)}`, lineColor),
            withinRangeSpreadTitle && getLineSpreadLegend(withinRangeSpreadTitle, lineColor),
            thresholdLabel && getLineLegend(thresholdLabel, lineColor, "dotted")));
    }, [getLineLegend, getLineSpreadLegend, inputSet.inputs, output?.operation?.parameters, workspaceSimulations]);
    const graphResultLabels = useMemo(() => {
        if (!graphResult) {
            return;
        }
        const { sourceAGraph, sourceBGraph, insightGraph, value, thresholdValue, withinRangeMax, withinRangeMin, } = graphResult;
        const withinRangeSpreadTitle = isNotEmpty(withinRangeMin) && isNotEmpty(withinRangeMax)
            ? `Range: ${renderNumberValue(withinRangeMin)}-${renderNumberValue(withinRangeMax)}`
            : undefined;
        return (React.createElement(React.Fragment, null,
            sourceAGraph &&
                getSourceLineLegend(sourceAGraph, withinRangeSpreadTitle, getThresholdLabel(thresholdValue)),
            sourceBGraph && getSourceLineLegend(sourceBGraph),
            insightGraph && getLineLegend("Point-by-point evaluation", colors.s600, "dashed"),
            value &&
                getLineLegend(output?.transformationOperation?.operationType?.label ??
                    "Point in time", colors.black)));
    }, [
        getLineLegend,
        getSourceLineLegend,
        getThresholdLabel,
        graphResult,
        output?.transformationOperation?.operationType,
    ]);
    const scoreLabel = useMemo(() => (React.createElement("div", { className: styles.score },
        React.createElement(ScoreIcon, { highlighted: true, ...prepareBaseScoreItem(score?.score, canDisplayScore) }),
        React.createElement("p", { className: styles["score-outputTitle"] },
            output?.type,
            " value"))), [canDisplayScore, output?.type, score?.score]);
    const renderLegendContent = useMemo(() => {
        if (!canDisplayScore) {
            return (React.createElement(React.Fragment, null,
                React.createElement(ScoreIcon, { highlighted: true }),
                React.createElement("p", { className: styles.incompleteInfo }, "Insight incomplete.")));
        }
        if (pending) {
            return (React.createElement("div", null,
                React.createElement(Loader, { size: 32, accented: true })));
        }
        return (React.createElement(React.Fragment, null,
            React.createElement(HtmlText, { targetTagName: "p", targetClassName: styles.operationSummaryText, text: operationSummaryText }),
            React.createElement("div", { className: styles.variable },
                React.createElement("p", { className: styles["variable-label"] }, "Variable"),
                selectedVariableLabel),
            graphResultLabels,
            scoreLabel,
            ((errorMessage && errored) || scoreHasBiasedValues) && (React.createElement("div", { className: styles.errorMessageWrapper },
                React.createElement("div", { className: styles.errorMessage },
                    React.createElement("div", { className: styles.iconWrapper },
                        React.createElement(WarningIcon, { className: styles.icon })),
                    React.createElement(HtmlText, { text: errorMessage ?? InsightLabels.BIASED_VALUES_SCORE_GRAPH, targetClassName: styles.text }))))));
    }, [
        canDisplayScore,
        errorMessage,
        errored,
        scoreHasBiasedValues,
        graphResultLabels,
        operationSummaryText,
        pending,
        scoreLabel,
        selectedVariableLabel,
    ]);
    useRoundTickValueObserver(plotRef);
    return (React.createElement(Dialog, { className: styles.dialog, title: `${index + 1}. ${output?.type}`, isOpen: true, primaryButton: { label: DIALOG_ACTION_TITLE.CLOSE, onClick: onClose } },
        React.createElement("div", { className: styles.content },
            React.createElement("div", { className: styles.graph },
                React.createElement("p", { className: styles["graph-yAxisTitle"] },
                    React.createElement("span", null, "Insight"),
                    selectedVariableLabel),
                React.createElement(PlotlyPlot, { ref: plotRef, config: plotConfig, data: graphData, layout: plotLayout, className: styles.plot }),
                React.createElement("p", { className: styles["graph-xAxisTitle"] },
                    "Time",
                    graphResult?.timeUnits ? ` (${graphResult.timeUnits.toLowerCase()})` : "")),
            React.createElement("div", { className: styles.legendWrapper },
                React.createElement(ScrollBar, null,
                    React.createElement("div", { className: styles.legend }, renderLegendContent))))));
};
export default OutputGraph;
